// @flow
import React, { type Element } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Button, RatingDisplay, OverlayTrigger, Tooltip, Row, Col } from 'angieslist-react-components';
import { RatingDetailsCondensed } from 'angieslist-composed-components';
import { Track } from 'angieslist-app-platform';
import ReactTruncate from 'react-truncate';
import { get } from 'lodash';
import ReviewRetort from './ReviewRetort';
import ReviewPropertiesList from './ReviewPropertiesList';
import ThumbnailImageCarousel from './ThumbnailImageCarousel';
import type { BrowserReducerState, Reviews } from '../../../types';
import './reviewCard.scss';

const thumbSizes = Object.freeze({
  xs: 2,
  sm: 3,
  md: 4,
  lg: 5,
  infinity: 5,
});
const thumbsPerPage = (browser: BrowserReducerState) => thumbSizes[browser.mediaType];

const descriptionToHide = ['unknown', '.', ''];
const reviewTextToHide = ['unknown', '.', ''];

type Props = {
  review: Reviews,
  results: number[],
  rank: number,
  browser: BrowserReducerState,
  serviceProviderId: string
};

type State = {
  collapsed: boolean,
};

class ReviewCardComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { collapsed: true };
  }
  state: State;
  props: Props;

  isCollapsed: (() => boolean) = () => this.state.collapsed;

  expand: ((event: any) => void) = (event: any) => {
    setTimeout(
      () => {
        this.setState(() => ({ collapsed: false }));
      },
      100,
    );
    event.preventDefault();
    event.stopPropagation();
  }

  collapse: ((event: any) => void) = (event: any) => {
    setTimeout(
      () => {
        this.setState(() => ({ collapsed: true }));
      },
      100,
    );
    event.preventDefault();
    event.stopPropagation();
  }

  reviewDescription: (() => null) = () => {
    const { review: { descriptionOfWork } } = this.props;
    if (!descriptionOfWork || !descriptionOfWork.display || this.isCollapsed()) {
      return null;
    }
    const showDescription = descriptionToHide.indexOf(descriptionOfWork.display) === -1;
    return (
      <If condition={showDescription}>
        <div className="review-card_description-of-work">
          <h5 className="review-card_section-heading">Description of work:</h5>
          <blockquote className="review-card_blockquote">
            {descriptionOfWork.display}
          </blockquote>
        </div>
      </If>
    );
  }

  reviewMedia(): Element<"div"> {
    const {
      browser,
      review: {
        media,
      },
    } = this.props;
    return (
      !this.isCollapsed() && media && media.length > 0 ?
        (
          <div className="review-card_images">
            <h3 className="review-card_section-heading">Review Images:</h3>
            <ul className="review-card_images-list">
              <ThumbnailImageCarousel
                images={media}
                thumbsPerPage={thumbsPerPage(browser)}
              />
            </ul>
          </div>
        ) : <div className="review-no_data" />
    );
  }

  handleExpand: ((e: any) => void) = (e: any) => this.expand(e)

  handleCollapse: ((e: any) => void) = (e: any) => this.collapse(e)

  render(): Element<"section"> {
    const {
      browser,
      results,
      rank,
      review,
      review: {
        costOfService,
        overallStarRating,
        reviewId,
        media,
        reportDate,
        text,
        willHireAgain,
        workCompleted,
        ratings,
        retort,
        userName: {
          firstName,
          lastName,
        },
      },
      serviceProviderId,
    } = this.props;
    const showReview = text && reviewTextToHide.indexOf(text.display) === -1;
    const collapsed = this.isCollapsed();
    const classes = classNames({
      'review-card': true,
      s_collapsed: this.isCollapsed(),
    });
    const retortText = get(retort, 'text.display');
    const reviewerName = `${firstName} ${lastName}`;
    const reviewRatingsArr = (ratings || [])
      .map(rating => ({
        ratingName: rating.ratingType,
        ratingValue: rating.starRating,
      }));
    const condensedRatingsDetailsFontSize = get(browser, 'lessThan.sm', false) ? '0.8em' : '1em';

    return (
      <section className={classes} aria-expanded={!collapsed} id={`review_${reviewId}`}>
        <Row>
          <Col xs={12} md={6}>
            <h5 className="review-card_review-head">
              {moment(reportDate.toString().split('T')[0]).format('MM/DD/YYYY')} - {reviewerName}
            </h5>
          </Col>
          <Col xs={12} md={6}>
            <RatingDisplay
              rating={overallStarRating}
              starSize="1.2em"
              customClass="review-card_review-head pull-right"
            />
          </Col>
        </Row>
        <If condition={media.length > 0}>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip
                id={`review-card--media-tooltip-${reviewId}`}
                bsStyle="primary-office"
              >
                This review has media attached
              </Tooltip>
            }
          >
            <span className="icon icon-image-indicator review-card_media-indicator" />
          </OverlayTrigger>
        </If>

        <If condition={showReview}>
          <blockquote className="review-card_review-quote">
            <i className="icon icon-quote-left" /><br />
            <ReactTruncate lines={collapsed ? 3 : 0} className="review-card_review-body">
              {text && text.display}
            </ReactTruncate>
          </blockquote>
        </If>

        {this.reviewDescription()}
        <Row>
          <Choose>
            <When condition={collapsed}>
              <Col xs={12}>
                <ReviewPropertiesList {...{ review, collapsed }} />
              </Col>
            </When>
            <When condition={!collapsed}>
              <Col xs={12} md={6} lg={7}>
                <ReviewPropertiesList {...{ review, collapsed }} />
              </Col>
              <Col xs={12} md={6} lg={5} style={{ paddingLeft: 0 }}>
                <RatingDetailsCondensed
                  ratingDetails={reviewRatingsArr}
                  customId="review-card_ratings-details"
                  maxWidth="20em"
                  ratingFontSize={condensedRatingsDetailsFontSize}
                  starSize={condensedRatingsDetailsFontSize}
                />
              </Col>
            </When>
          </Choose>
        </Row>
        <ReviewRetort
          reviewId={reviewId}
          serviceProviderId={serviceProviderId}
          status={get(review, 'retort.status')}
          text={retortText}
        />

        {this.reviewMedia()}

        {collapsed ?
          <Track>
            <Button
              id={`review-card_expand-${reviewId}`}
              className="review-card_expand"
              bsStyle="link"
              bsSize="small"
              flat
              tabIndex={0}
              onClick={this.handleExpand}
              data-track-review-id={reviewId}
              data-track-rating={overallStarRating}
              data-track-rank={rank}
              data-track-service-performed={workCompleted}
              data-track-hire-again={willHireAgain}
              data-track-cost-of-service={costOfService}
              data-track-more-or-less="More"
              data-track-results={results}
            >
              <span className="review-card_read-more">
                {'Read More '}<i className="icon icon-right-open" />
              </span>
            </Button>
          </Track>
          :
          <Track>
            <Button
              id={`review-card_collapse-${reviewId}`}
              className="review-card_collapse"
              bsStyle="link"
              bsSize="small"
              tabIndex={0}
              onClick={this.handleCollapse}
              data-track-review-id={reviewId}
              data-track-rating={overallStarRating}
              data-track-rank={rank}
              data-track-service-performed={workCompleted}
              data-track-hire-again={willHireAgain}
              data-track-cost-of-service={costOfService}
              data-track-more-or-less="Less"
              data-track-results={results}
            >
              <span className="review-card_close">
                Close&nbsp;<i className="icon icon-up-open" />
              </span>
            </Button>
          </Track>
        }
      </section>
    );
  }
}

export default ReviewCardComponent;



// WEBPACK FOOTER //
// ./src/modules/store/browser/components/reviews/ReviewCard.js